import Drawflow from "drawflow";
import React, { memo, useEffect } from "react";

import useGetWorkflowElementById from "@/hooks/requests/workflowElements/use-get-workflow-element-backoffice-by-id";
import { IWorkflowStep } from "@/interfaces/workflow-step.interface";
import { IWorkflow } from "@/interfaces/workflow.interface";

import { calculateNodePosition, generateNodeHTML, initializeConnections } from "./constants";

type WorkflowNodeProps = {
  workflow: IWorkflow | null;
  step: IWorkflowStep;
  editor: Drawflow | null;
  handleOpen: (selected?: unknown) => void;
  setWorkflowStep: React.Dispatch<React.SetStateAction<IWorkflowStep | null>>;
};

const WorkflowNodeComponent = ({
  workflow,
  step,
  editor,
  handleOpen,
  setWorkflowStep,
}: WorkflowNodeProps) => {
  const { data: element } = useGetWorkflowElementById({
    _id: step.workflowElementId || 1,
  });

  useEffect(() => {
    if (!element) return;

    if (element && editor && workflow) {
      const expectedId = `step-${step.id}`;

      if (!document.getElementById(expectedId)) {
        const { workflowPositionX: x, workflowPositionY: y, stepNumber } = step;

        const hasPosition = x && y;
        const _stepNumber = Number(stepNumber);
        const position = hasPosition ? { x, y } : calculateNodePosition(_stepNumber);

        const nodeId = editor.addNode(
          `Step ${stepNumber}`,
          1,
          1,
          position.x,
          position.y,
          "",
          { ...step },
          generateNodeHTML(step, element),
          false
        );

        step.nodeId = nodeId;

        const nodeElement = document.getElementById(`node-${nodeId}`);

        if (nodeElement) {
          nodeElement.addEventListener("dblclick", () => {
            handleOpen();
            setWorkflowStep(step);
          });
        }

        workflow.workflowSteps.forEach((step) => initializeConnections(step, workflow, editor));
      }
    }
  }, [editor, element, handleOpen, setWorkflowStep, step, workflow]);

  return null;
};

WorkflowNodeComponent.displayName = "WorkflowNode";

export const WorkflowNode = memo(WorkflowNodeComponent, (prevProps, nextProps) => {
  return prevProps.step.workflowElementId === nextProps.step.workflowElementId;
});
